import { createContext, useCallback, useContext, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';

/**
 * Tiny history-based router. Enough for the prototype and keeps the app
 * dependency-free; screens only use `useNavigate`, `useRoute` and `Link`.
 */
interface RouterValue {
  path: string;
  search: URLSearchParams;
  navigate: (to: string, opts?: { replace?: boolean; state?: unknown }) => void;
  back: () => void;
  state: unknown;
}

const RouterContext = createContext<RouterValue | null>(null);

function currentPath(): string {
  return window.location.pathname || '/';
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(currentPath);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search));
  const [state, setState] = useState<unknown>(() => window.history.state);

  useEffect(() => {
    const onPop = () => {
      setPath(currentPath());
      setSearch(new URLSearchParams(window.location.search));
      setState(window.history.state);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, opts: { replace?: boolean; state?: unknown } = {}) => {
    const url = new URL(to, window.location.origin);
    if (opts.replace) window.history.replaceState(opts.state ?? null, '', url);
    else window.history.pushState(opts.state ?? null, '', url);
    setPath(url.pathname);
    setSearch(new URLSearchParams(url.search));
    setState(opts.state ?? null);
    // Move focus to the top of the new screen for keyboard and screen-reader users.
    requestAnimationFrame(() => {
      const main = document.getElementById('main');
      if (main) {
        main.focus({ preventScroll: true });
        main.scrollTo?.({ top: 0 });
      }
      window.scrollTo({ top: 0 });
    });
  }, []);

  const back = useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else navigate('/tree');
  }, [navigate]);

  const value = useMemo(() => ({ path, search, navigate, back, state }), [path, search, navigate, back, state]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const v = useContext(RouterContext);
  if (!v) throw new Error('RouterProvider missing');
  return v;
}

export function useNavigate() {
  return useRouter().navigate;
}

/** Match `/people/:id` style patterns. Returns params or null. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p[p.length - 1] === '*') {
    if (a.length < p.length - 1) return null;
  } else if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i] === '*') {
      params['*'] = a.slice(i).join('/');
      break;
    }
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

export function Link({ to, children, className, replace, state, onClick, ariaCurrent, ...rest }: { to: string; children: ReactNode; className?: string; replace?: boolean; /** Carried to the next screen as `useRouter().state` — how Admin controls knows its menu is one step back. */ state?: unknown; onClick?: () => void; ariaCurrent?: 'page' | undefined; title?: string; style?: import('react').CSSProperties; 'aria-label'?: string; /** Anchor id the assistant can point at — see assistant/anchors.ts. */ 'data-assist'?: string }) {
  const { navigate } = useRouter();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onClick?.();
    navigate(to, { replace, state });
  };
  return (
    <a href={to} className={className} onClick={handle} aria-current={ariaCurrent} {...rest}>
      {children}
    </a>
  );
}
